import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * AlignmentSelector Component
 *
 * Provides a three-button interface for selecting text alignment (left, center, right)
 * Shows visual indication when alignment has been manually overridden from GPT-5 Vision default
 *
 * Features:
 * - Three alignment options with icons
 * - Active state indication
 * - Override badge when manually changed
 * - Disabled state support
 * - Accessible keyboard navigation
 */
export const AlignmentSelector = ({
  currentAlignment = 'left',
  originalAlignment = 'left',
  onAlignmentChange,
  disabled = false
}) => {
  const { t } = useTranslation('approval');
  const isOverridden = currentAlignment !== originalAlignment;

  const alignments = [
    {
      value: 'left',
      label: t('alignment.options.left'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h14" />
        </svg>
      )
    },
    {
      value: 'center',
      label: t('alignment.options.center'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M6 18h12" />
        </svg>
      )
    },
    {
      value: 'right',
      label: t('alignment.options.right'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M6 18h14" />
        </svg>
      )
    }
  ];

  const handleClick = (alignment) => {
    if (!disabled && onAlignmentChange) {
      onAlignmentChange(alignment);
    }
  };

  return (
    <div className="alignment-selector">
      {/* Header with override indicator */}
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          {t('alignment.title')}
        </label>
        {isOverridden && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            {t('alignment.adjusted')}
          </span>
        )}
      </div>

      {/* Alignment buttons */}
      <div className="inline-flex rounded-lg border border-gray-300 bg-white shadow-sm" role="group">
        {alignments.map((alignment, index) => {
          const isActive = currentAlignment === alignment.value;
          const isFirst = index === 0;
          const isLast = index === alignments.length - 1;

          return (
            <button
              key={alignment.value}
              type="button"
              onClick={() => handleClick(alignment.value)}
              disabled={disabled}
              className={`
                px-4 py-2
                text-sm font-medium
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                ${isFirst ? 'rounded-l-lg' : ''}
                ${isLast ? 'rounded-r-lg' : ''}
                ${!isFirst && !isLast ? 'border-x border-gray-300' : ''}
                ${isActive
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                }
                ${disabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
                }
              `}
              title={alignment.label}
              aria-label={alignment.label}
              aria-pressed={isActive}
            >
              <div className="flex items-center gap-2">
                {alignment.icon}
                <span>{alignment.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Helper text */}
      <p className="mt-2 text-xs text-gray-500">
        {isOverridden
          ? t('alignment.defaultBy', { alignment: alignments.find(a => a.value === originalAlignment)?.label || originalAlignment })
          : t('alignment.recommended')
        }
      </p>

      {/* Screen reader info */}
      <div className="sr-only">
        Text alignment selector. Current alignment: {currentAlignment}.
        {isOverridden && ` Original GPT-5 recommendation was ${originalAlignment}.`}
      </div>
    </div>
  );
};

AlignmentSelector.propTypes = {
  currentAlignment: PropTypes.oneOf(['left', 'center', 'right']).isRequired,
  originalAlignment: PropTypes.oneOf(['left', 'center', 'right']).isRequired,
  onAlignmentChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export default AlignmentSelector;
